import React from 'react';
import theme from './ItemCategorySelect.module.scss';
import { useSelector } from 'react-redux';
import { t } from '@lingui/core/macro';
import { IState } from '@/state/IState';
import { ItemCategory } from '@/types';
import { components, FilterOptionOption } from 'react-select';
import { Select } from '@ygtechnologies/bygr-frontend-component-library';

interface Props {
    value: number | null | undefined;
    className?: string;
    onChange?;
    required?: boolean;
    fieldName?: string;
    rootItemCategoryFilter?(itemCategory: ItemCategory): boolean;
    showNoneOption?: boolean;
    isDisabled?: boolean;
}

interface SelectItem {
    label: string;
    value: number | null | undefined;
    options?: SelectItem[];
    groupName?: string;
}

const ValueContainer = (props) => {
    const selected: SelectItem | null = props.getValue()[0];

    if (!selected) {
        return <components.ValueContainer {...props} />;
    }

    let group: SelectItem | null = null;
    const options: SelectItem[] = props.options;
    const inputValue = props.selectProps.inputValue;

    if (props.hasValue) {
        group = options.find((o) => o.options && o.options.some((x) => x.value === selected!.value)) ?? null;
    }

    if (!group) {
        return <components.ValueContainer {...props} className={theme.ungrouped} />;
    }

    return (
        <>
            {props.hasValue && inputValue === '' && group && <div className={theme.groupLabel}>{group.label as string}</div>}
            {/* <div className={theme.test}> */}
            <components.ValueContainer {...props} />
            {/* </div> */}
        </>
    );
};

const ItemCategorySelect: React.FC<Props> = ({
    value,
    rootItemCategoryFilter = () => true,
    showNoneOption = true,
    ...rest
}) => {
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const uncategorizedOption = { label: t`No category`, value: null };

    if (!itemCategories) {
        return <Select {...rest} options={[{ label: t`Loading`, value: '' }]} />;
    }

    let options: SelectItem[] = itemCategories
        .filter(rootItemCategoryFilter)
        .filter((cat) => cat.subCategories.length > 0)
        .map((topCat) => ({
            label: topCat.name,
            value: undefined,
            options: topCat.subCategories.map((cat) => ({
                label: cat.name,
                value: cat.id,
                groupName: topCat.name,
            })),
        }));

    if (showNoneOption) {
        options.splice(0, 0, uncategorizedOption);
    }

    const selectedItem =
        value === null ? uncategorizedOption : options.flatMap((o) => o.options).find((o) => o && o.value === value);

    /**
     * Custom filter to be able to filter on GroupName as well.
     * Will ignore "No Category" since that was the simplest way to do it.
     */
    const filterOption = (option: FilterOptionOption<SelectItem>, value: string) => {
        if (!value) return true;
        if (option.value === null) return false;
        const lowerCaseValue = value.toLowerCase();

        return (
            (option.data.groupName?.toLowerCase() ?? '').includes(lowerCaseValue) ||
            option.label.toLowerCase().includes(lowerCaseValue)
        );
    };

    return (
        <Select
            styles={{
                control: (provided) => ({
                    ...provided,
                    minHeight: '42px',
                }),
                singleValue: (provided) => ({
                    ...provided,
                    top: 'calc(50% + 6px)',
                }),
                input: (provided) => ({
                    ...provided,
                    top: 'calc(50% + 6px)',
                }),
            }}
            placeholder={t`Please select`}
            value={selectedItem}
            filterOption={filterOption}
            components={{ ValueContainer }}
            {...rest}
            options={options}
        />
    );
};

export default ItemCategorySelect;
